import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { Table } from '@mantine/core';
import { withColumnWidth } from '../columnState';
import classes from '../../common/ConfigurableTable/ConfigurableTable.module.css';

interface ColumnWidthCellProps {
  /** The column's default style (min width, wrapping) */
  style: CSSProperties;
  /** The saved width of the column, if the user resized it */
  width: number | undefined;
  className?: string;
  /** The content has its own Tooltip, so the cell adds no title over it */
  hasTooltip?: boolean;
  children: ReactNode;
}

/**
 * MED-1308: a Data Library body cell with the same width as its header.
 * A cell that does not wrap ends in an ellipsis when its text is cut,
 * and shows the full text on hover. A cell that wraps is not changed.
 */
export function ColumnWidthCell({ style, width, className, hasTooltip = false, children }: ColumnWidthCellProps) {
  const clips = style.whiteSpace === 'nowrap';
  return (
    <Table.Td
      className={clips ? `${className ?? ''} ${classes.clippedCell}` : className}
      style={withColumnWidth(style, width)}
      onMouseEnter={clips && !hasTooltip ? showFullTextIfCut : undefined}
    >
      {children}
    </Table.Td>
  );
}

function showFullTextIfCut(event: MouseEvent<HTMLTableCellElement>) {
  const cell = event.currentTarget;
  const cut = [cell, ...Array.from(cell.querySelectorAll('*'))].some(el => el.scrollWidth > el.clientWidth + 1);
  cell.title = cut ? cell.innerText.replace(/\s+/g, ' ').trim() : '';
}
